import { Alert, Button, Input, Select, Space } from 'antd'
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useState, useMemo } from 'react'
import { useTenants } from '../hooks/useTenants'
import { TenantTable } from './TenantTable'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { CreateTenantModal } from './CreateTenantModal'
import { TenantDetailDrawer } from './TenantDetailDrawer'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin, isWorkspaceAdmin } from '../../../utils/roles'

export function TenantList() {
  const { user } = useAuth()
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<Array<'new' | 'demo' | 'trial' | 'uat'>>([])
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedTenantCode, setSelectedTenantCode] = useState<string | null>(null)
  const { tenants, loading, error, refresh } = useTenants({
    includeInactive: statusFilter !== 'active',
    includeFeatureFlags: true,
    includeUserCount: false
  })

  const canCreateWorkspace = isSysAdmin(user) || isWorkspaceAdmin(user)

  // Filter tenants based on search term and selected types
  const filteredTenants = useMemo(() => {
    const search = searchTerm.toLowerCase().trim()
    const typeSet = new Set(selectedTypes)
    return tenants.filter(tenant => {
      if (search) {
        const matchesSearch =
          tenant.tenantName.toLowerCase().includes(search) ||
          tenant.workspaceCode.toLowerCase().includes(search) ||
          tenant.tenantCode.toLowerCase().includes(search)
        if (!matchesSearch) return false
      }
      if (typeSet.size > 0) {
        if (!tenant.workspaceType || !typeSet.has(tenant.workspaceType)) return false
      }
      if (statusFilter === 'inactive' && !tenant.inactiveDate) return false
      return true
    })
  }, [tenants, searchTerm, selectedTypes, statusFilter])

  if (error) {
    return (
      <Alert
        message="Error Loading Workspaces"
        description={error.message}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={refresh}>
            Retry
          </Button>
        }
      />
    )
  }

  if (loading && tenants.length === 0) {
    return <Loading />
  }

  if (!loading && tenants.length === 0) {
    return <EmptyState description="No workspaces found" />
  }

  return (
    <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 112px)' }}>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0
        }}
      >
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <Space size="middle" wrap>
            <Input
              placeholder="Search by name or code..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 350 }}
              allowClear
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="All types"
              value={selectedTypes}
              onChange={setSelectedTypes}
              style={{ minWidth: 220 }}
              options={[
                { label: 'New', value: 'new' },
                { label: 'Demo', value: 'demo' },
                { label: 'Trial', value: 'trial' },
                { label: 'UAT', value: 'uat' }
              ]}
              maxTagCount="responsive"
            />
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            {canCreateWorkspace && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                New Workspace
              </Button>
            )}
          </Space>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ marginLeft: 'auto', width: 150 }}
            options={[
              { label: 'Active only', value: 'active' },
              { label: 'Inactive only', value: 'inactive' },
              { label: 'All', value: 'all' }
            ]}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {filteredTenants.length === 0 && searchTerm ? (
            <EmptyState description={`No workspaces found matching "${searchTerm}"`} />
          ) : (
            <TenantTable
              tenants={filteredTenants}
              loading={loading}
              showInactive={statusFilter !== 'active'}
              onRowClick={tenantCode => setSelectedTenantCode(tenantCode)}
              selectedTenantCode={selectedTenantCode}
            />
          )}
        </div>
        {canCreateWorkspace && (
          <CreateTenantModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={refresh} />
        )}
      </div>
      {selectedTenantCode && (
        <div
          style={{
            width: '500px',
            flexShrink: 0,
            borderLeft: '1px solid #d9d9d9',
            overflow: 'auto',
            backgroundColor: '#ffffff',
            borderRadius: '8px'
          }}
        >
          <TenantDetailDrawer tenantCode={selectedTenantCode} onClose={() => setSelectedTenantCode(null)} />
        </div>
      )}
    </div>
  )
}
